import { useLang } from "../context/LanguageContext";
import { Seo } from "../components/Seo";
import { CONTACT, waHref } from "../i18n/translations";
import { Mail, MessageCircle, MapPin } from "lucide-react";
import { openConsentSettings } from "../lib/consent";

// A single, discreet legal-notice page shared across en/fr/tr locales.
// The route pattern `/mentions-legales` is used in all three languages so that a
// French Riviera visitor lands on the same URL from any locale toggle.
export default function LegalMentions() {
  const { t } = useLang();
  const waLink = waHref("");
  const updated = "2026-10-24";

  return (
    <div data-testid="legal-page" className="min-h-screen bg-background text-foreground pt-32 pb-24">
      <Seo
        title={`${t("legal.title")} | Velmira Living`}
        description={t("legal.subtitle")}
      />
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-3xl">
        <div className="mb-14">
          <p className="font-accent text-[11px] uppercase tracking-[0.35em] text-primary mb-3">
            {t("footer.legalTitle")}
          </p>
          <h1 className="font-headings text-4xl sm:text-5xl text-foreground leading-tight">
            {t("legal.title")}
          </h1>
          <p className="mt-4 font-body font-light text-foreground/60 max-w-xl leading-relaxed">
            {t("legal.subtitle")}
          </p>
        </div>

        <div className="space-y-14">
          {/* Publisher */}
          <section data-testid="legal-publisher">
            <h2 className="font-accent text-xs uppercase tracking-[0.25em] text-primary mb-4">
              {t("legal.publisherTitle")}
            </h2>
            <p className="font-body font-light text-foreground/80 leading-relaxed">
              {t("legal.publisherText")}
            </p>
          </section>

          {/* Identifiers */}
          <section data-testid="legal-identifiers">
            <h2 className="font-accent text-xs uppercase tracking-[0.25em] text-primary mb-4">
              {t("legal.identifiersTitle")}
            </h2>
            <dl className="border border-border/60 bg-secondary divide-y divide-border/60">
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 px-5 py-4">
                <dt className="font-accent text-[11px] uppercase tracking-[0.25em] text-muted-foreground sm:w-32 shrink-0">
                  Company
                </dt>
                <dd className="font-body text-foreground">Velmira Living</dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 px-5 py-4">
                <dt className="font-accent text-[11px] uppercase tracking-[0.25em] text-muted-foreground sm:w-32 shrink-0">
                  SIRET
                </dt>
                <dd className="font-body text-foreground font-mono tracking-wider">953 630 084 00010</dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 px-5 py-4">
                <dt className="font-accent text-[11px] uppercase tracking-[0.25em] text-muted-foreground sm:w-32 shrink-0">
                  RSAC
                </dt>
                <dd className="font-body text-foreground font-mono tracking-wider">937 000 002 071 316 661</dd>
              </div>
            </dl>
          </section>

          {/* Contact */}
          <section data-testid="legal-contact">
            <h2 className="font-accent text-xs uppercase tracking-[0.25em] text-primary mb-4">
              {t("legal.contactTitle")}
            </h2>
            <ul className="flex flex-col gap-3 font-body font-light text-foreground/80">
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="inline-flex items-center gap-3 hover:text-primary transition-colors"
                >
                  <Mail size={16} className="text-primary" /> {CONTACT.email}
                </a>
              </li>
              <li>
                <a
                  href={waLink}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-3 hover:text-primary transition-colors"
                >
                  <MessageCircle size={16} className="text-primary" /> {CONTACT.whatsapp}
                </a>
              </li>
              <li className="inline-flex items-center gap-3">
                <MapPin size={16} className="text-primary" /> {t("contact.location")}
              </li>
            </ul>
          </section>

          {/* Hosting */}
          <section data-testid="legal-hosting">
            <h2 className="font-accent text-xs uppercase tracking-[0.25em] text-primary mb-4">
              {t("legal.hostingTitle")}
            </h2>
            <p className="font-body font-light text-foreground/80 leading-relaxed">
              {t("legal.hostingText")}
            </p>
          </section>

          {/* Cookies & analytics */}
          <section data-testid="legal-cookies">
            <h2 className="font-accent text-xs uppercase tracking-[0.25em] text-primary mb-4">
              {t("legal.cookiesTitle")}
            </h2>
            <p className="font-body font-light text-foreground/80 leading-relaxed">{t("legal.cookiesIntro")}</p>
            <h3 className="mt-8 font-accent text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{t("legal.cookiesServicesTitle")}</h3>
            <dl className="mt-3 border border-border/60 bg-secondary divide-y divide-border/60">
              {(t("legal.cookiesServices") || []).map((svc, i) => (
                <div key={i} className="px-5 py-4" data-testid={`legal-cookies-service-${i}`}>
                  <dt className="font-body text-foreground">{svc.name}</dt>
                  <dd className="mt-2 font-body font-light text-sm text-foreground/75 leading-relaxed">
                    {svc.purpose} {svc.cookies}{" "}
                    <a href={svc.link} target="_blank" rel="noopener noreferrer" className="underline decoration-primary/40 underline-offset-4 hover:text-primary" data-testid={`legal-cookies-link-${i}`}>
                      {svc.linkLabel}
                    </a>
                  </dd>
                </div>
              ))}
            </dl>
            <h3 className="mt-8 font-accent text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{t("legal.cookiesEssentialTitle")}</h3>
            <p className="mt-3 font-body font-light text-foreground/80 leading-relaxed">{t("legal.cookiesEssentialText")}</p>
            <h3 className="mt-8 font-accent text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{t("legal.cookiesChoiceTitle")}</h3>
            <p className="mt-3 font-body font-light text-foreground/80 leading-relaxed">{t("legal.cookiesChoiceText")}</p>
            <button
              type="button"
              onClick={openConsentSettings}
              data-testid="legal-cookie-settings"
              className="mt-5 border border-foreground/30 text-foreground hover:border-primary hover:text-primary px-5 py-3 font-accent text-[11px] uppercase tracking-[0.2em] transition-colors"
            >
              {t("legal.cookiesButton")}
            </button>
          </section>

          {/* Content / IP / Data */}
          <section data-testid="legal-content">
            <h2 className="font-accent text-xs uppercase tracking-[0.25em] text-primary mb-4">
              {t("legal.contentTitle")}
            </h2>
            <p className="font-body font-light text-foreground/80 leading-relaxed">
              {t("legal.contentText")}
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-border/50 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-body text-xs text-muted-foreground">
            © {new Date().getFullYear()} Velmira Living
          </p>
          <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            {t("legal.updated")}: {updated}
          </p>
        </div>
      </div>
    </div>
  );
}
